import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import BdsInput from '../../../../../../shared/components/v3/BdsInput/BdsInput';
import BdsTooltip from '../../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import BdsButton from '../../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import { CHECK_CIRCLE_V2, XERROR_ICON } from '../../../../../../shared/constants/images';
import { FLEXIBLE_REMUNERATION_REQUEST_STATUS } from '../../../../../../shared/constants/requests';
import { CompanyContext } from '../../../../../../shared/context/company/companyContext';
import { IFlexibleRemunerationRequest } from '../../../../../../shared/interfaces/company';
import { TRANSLATIONS_REQUESTS } from '../../../../../../shared/translations/requests';
import { GenericVoidFunction } from '../../../../../../shared/types/genericTypes';
import {
  formatAsDDMMYYYYInput,
  getEndOfCurrentYearDDMMYYYY,
  isAfter,
  isBefore,
  validateDDMMYYYY,
  validateOptionalDDMMYYYY
} from '../../../../../../shared/utils/datetime/datetime';
import { invertDateFormat, toLocaleNumberFormat } from '../../../../../../shared/utils/utils';
import './InsuranceRequestDiscounts.scss';

interface InsuranceRequestDiscountsProps {
  request: IFlexibleRemunerationRequest;
  onApprove: (dates: { startDate: string; renewalDate: string }) => void;
  onReject: GenericVoidFunction;
  onCancel: GenericVoidFunction;
}

const InsuranceRequestDiscounts = ({ request, onApprove, onReject, onCancel }: InsuranceRequestDiscountsProps) => {
  const { t } = useTranslation();
  const { locale: companyLocale } = useContext(CompanyContext);
  const { amount: totalAmount, status, currency, renewalDate, startDate: requestStartDate } = request;
  const [startDate, setStartDate] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<string>(getEndOfCurrentYearDDMMYYYY());
  const isPending = status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.pending.value;
  const isActive = status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.active.value;
  const isRejected = status === FLEXIBLE_REMUNERATION_REQUEST_STATUS.rejected.value;

  const formattedTotalAmount = useMemo(
    () =>
      toLocaleNumberFormat(
        totalAmount,
        { style: 'currency', currency: currency, maximumFractionDigits: 2 },
        companyLocale || undefined
      ),
    [totalAmount, companyLocale, currency]
  );

  const endParsed = useMemo(() => validateOptionalDDMMYYYY(expirationDate), [expirationDate]);

  const startParsed = useMemo(() => validateOptionalDDMMYYYY(startDate), [startDate]);

  const validateStartDate = useCallback(
    (value: string | null) => {
      if (!value || value.length < 10) return null;
      const result = validateDDMMYYYY(value);
      if (!result.ok) {
        return result.reason === 'FORMAT'
          ? t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.dateFormatError)
          : t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.dateError);
      }
      if (isAfter(result.date, endParsed)) {
        return t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.startDateError);
      }

      return null;
    },
    [endParsed, t]
  );

  const validateExpirationDate = useCallback(
    (value: string | null) => {
      if (!value || value.length < 10) return null;
      const result = validateDDMMYYYY(value);
      if (!result.ok) {
        return result.reason === 'FORMAT'
          ? t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.dateFormatError)
          : t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.dateError);
      }
      if (isBefore(result.date, startParsed)) {
        return t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.expirationDateError);
      }

      return null;
    },
    [startParsed, t]
  );

  const isFormValid = useMemo(() => {
    if (startDate.length !== 10 || expirationDate.length !== 10) return false;

    const start = validateOptionalDDMMYYYY(startDate);
    const end = validateOptionalDDMMYYYY(expirationDate);

    if (!start || !end) return false;
    if (isAfter(start, end)) return false;

    return true;
  }, [startDate, expirationDate]);

  const callApprove = useCallback(() => {
    if (!isFormValid) return;
    onApprove({ startDate, renewalDate: expirationDate });
  }, [isFormValid, onApprove, startDate, expirationDate]);

  const renderInputsValues = useCallback(() => {
    return (
      <>
        <div className="ird-input">
          <BdsInput
            labelText={t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.startDateLabel)}
            hintText={t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.globalDatePlaceholder)}
            initValue={startDate}
            onChanged={setStartDate}
            maxLength={10}
            inputFormatters={[formatAsDDMMYYYYInput]}
            validator={validateStartDate}
          />
        </div>
        <div className="ird-input">
          <BdsInput
            labelText={t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.expirationDateLabel)}
            hintText={t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.globalDatePlaceholder)}
            initValue={expirationDate}
            onChanged={setExpirationDate}
            maxLength={10}
            inputFormatters={[formatAsDDMMYYYYInput]}
            validator={validateExpirationDate}
          />
        </div>
      </>
    );
  }, [expirationDate, startDate, t, validateExpirationDate, validateStartDate]);

  const renderApproveButton = useCallback(() => {
    return (
      <BdsButton variant="primary" size="m" action={callApprove} iconLeft={CHECK_CIRCLE_V2} disabled={!isFormValid}>
        {t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.approveButton)}
      </BdsButton>
    );
  }, [callApprove, isFormValid, t]);

  const renderTextValues = useCallback(() => {
    return (
      <>
        <div className="ird-field date">
          <p className="label">{t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.startDateLabel)}</p>
          <p className="value">{requestStartDate ? invertDateFormat(requestStartDate, '-', '/', true, true) : ''}</p>
        </div>
        <div className="ird-field date">
          <p className="label">{t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.expirationDateLabel)}</p>
          <p className="value">{renewalDate ? invertDateFormat(renewalDate, '-', '/', true, true) : ''}</p>
        </div>
      </>
    );
  }, [renewalDate, requestStartDate, t]);

  return (
    <div className="flex flex-col gap-11" data-experience="new" data-testid="material-request-discounts">
      <div className="insurance-request-discount">
        <div className="flex flex-col gap-2">
          <div className="ird-title">{t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.title)}</div>
          {isPending && (
            <p className="ird-description">{t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.description)}</p>
          )}
        </div>
        <div className="ird-content">
          <div className="ird-field">
            <p className="label">{t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.totalAmountLabel)}</p>
            <p className="value">{formattedTotalAmount}</p>
          </div>
          {!isRejected && <div className="ird-inputs">{isPending ? renderInputsValues() : renderTextValues()}</div>}
        </div>
      </div>
      {isPending && (
        <div className="flex justify-end gap-11">
          <BdsButton variant="tertiary" size="m" action={onReject} iconLeft={XERROR_ICON} disabled={false}>
            {t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.rejectButton)}
          </BdsButton>
          {isFormValid ? (
            renderApproveButton()
          ) : (
            <BdsTooltip
              message={t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.tooltipError)}
              position="top-left"
              variant="standard"
            >
              {renderApproveButton()}
            </BdsTooltip>
          )}
        </div>
      )}
      {isActive && (
        <div className="flex justify-end">
          <BdsButton variant="secondary" size="m" action={onCancel} iconLeft={XERROR_ICON}>
            {t(TRANSLATIONS_REQUESTS.details.insuranceDiscounts.cancelButton)}
          </BdsButton>
        </div>
      )}
    </div>
  );
};

export default InsuranceRequestDiscounts;
